import { Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { ProductPerformanceRowDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

type ProductPerformanceTableProps = {
  rows?: ProductPerformanceRowDTO[];
  isLoading?: boolean;
  variant?: "best" | "worst";
  embedded?: boolean;
};

export function ProductPerformanceTable({
  rows = [],
  isLoading,
  variant = "best",
  embedded = false,
}: ProductPerformanceTableProps) {
  const isWorst = variant === "worst";

  return (
    <div className="brand-card overflow-hidden">
      {embedded ? null : (
        <div className="border-secondary/15 border-b px-4 py-3 sm:px-5">
          <h2 className="font-display text-lg">
            {isWorst ? "ສິນຄ້າຂາຍຊ້າ / ບໍ່ອອກ" : "ສິນຄ້າຂາຍດີ"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {isWorst
              ? "variant ທີ່ມີສະຕັອກແຕ່ຂາຍໜ້ອຍຫຼືບໍ່ຂາຍ — ຈຳນວນເປັນຫນ່ວຍຂາຍ"
              : "ອັນດັບ variant ຕາມຍອດຂາຍ — ຈຳນວນເປັນຫນ່ວຍຂາຍ"}
          </p>
        </div>
      )}
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
      ) : (
        <>
          <div className="flex flex-col gap-3 p-4 md:hidden">
            {rows.map((row) => (
              <ReportMobileCard key={row.variantId}>
                <ReportProductLink
                  productId={row.productId}
                  productName={row.productName}
                  variantName={row.variantName}
                />
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  {isWorst ? (
                    <ReportStat
                      label="ສະຕັອກ"
                      value={formatQtyWithUnit(
                        row.currentStock ?? 0,
                        row.stockUnitLabel,
                      )}
                    />
                  ) : null}
                  <ReportStat
                    label="ຂາຍ"
                    value={
                      row.quantity === 0 ? (
                        <span className="text-destructive">0</span>
                      ) : (
                        formatQtyWithUnit(row.quantity, row.stockUnitLabel)
                      )
                    }
                  />
                  <ReportStat
                    label="ແຖມ"
                    value={
                      row.bonusQuantity > 0
                        ? formatQtyWithUnit(
                            row.bonusQuantity,
                            row.stockUnitLabel,
                          )
                        : "—"
                    }
                    valueClassName="text-muted-foreground"
                  />
                  <ReportStat
                    label="ຍອດ"
                    value={formatLak(row.revenue)}
                    className={isWorst ? undefined : "col-span-2"}
                  />
                </dl>
              </ReportMobileCard>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-secondary/10 border-b text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium sm:px-5">ສິນຄ້າ</th>
                  {isWorst ? (
                    <th className="px-4 py-2 text-right font-medium sm:px-5">
                      ສະຕັອກ
                    </th>
                  ) : null}
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຂາຍ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ແຖມ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຍອດ
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.variantId}
                    className="border-secondary/5 border-b last:border-0"
                  >
                    <td className="px-4 py-2.5 sm:px-5">
                      <ReportProductLink
                        productId={row.productId}
                        productName={row.productName}
                        variantName={row.variantName}
                      />
                    </td>
                    {isWorst ? (
                      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                        {formatQtyWithUnit(
                          row.currentStock ?? 0,
                          row.stockUnitLabel,
                        )}
                      </td>
                    ) : null}
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {row.quantity === 0 ? (
                        <span className="text-destructive">0</span>
                      ) : (
                        formatQtyWithUnit(row.quantity, row.stockUnitLabel)
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground tabular-nums sm:px-5">
                      {row.bonusQuantity > 0
                        ? formatQtyWithUnit(
                            row.bonusQuantity,
                            row.stockUnitLabel,
                          )
                        : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {formatLak(row.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
